import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/* La feuille du bas du mode école (comme une feuille iOS) : un titre, son contenu qui défile,
   et ses boutons toujours visibles en bas, au pouce.

   Elle est posée dans un portail, directement sous <body>, au-dessus de tout (z 80). Rendue
   à l'intérieur de l'écran qui défile, elle restait SOUS la barre d'onglets sur iPhone :
   « Créer les 4 factures à générer ? » s'affichait sans ses boutons (Jen, 01.11.2026 ; même
   cause que l'éditeur des disponibilités de l'app prof le 31.10.2026). Le bas respecte la
   zone de l'iPhone (env(safe-area-inset-bottom)) et la feuille ne dépasse jamais l'écran :
   son contenu défile, ses boutons restent en place. */
export const Feuille = ({ titre, sousTitre, children, pied, onFermer, occupe = false, testId = "ecole-feuille" }) => {
  useEffect(() => {
    const echap = (e) => { if (e.key === "Escape" && !occupe) onFermer?.(); };
    window.addEventListener("keydown", echap);
    // L'écran du dessous ne défile plus pendant que la feuille est ouverte.
    const fond = document.getElementById("ecole-defilement");
    const avant = fond?.style.overflowY;
    if (fond) fond.style.overflowY = "hidden";
    return () => {
      window.removeEventListener("keydown", echap);
      if (fond) fond.style.overflowY = avant || "";
    };
  }, [onFermer, occupe]);
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(15,27,45,0.45)", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      onClick={() => { if (!occupe) onFermer?.(); }}>
      <div role="dialog" aria-modal="true" aria-label={titre}
        className="w-full max-w-lg bg-white rounded-t-[28px] flex flex-col shadow-2xl"
        style={{ maxHeight: "100%", color: "var(--text)" }}
        onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start gap-3 px-5 pt-5 pb-2 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-semibold leading-snug">{titre}</div>
            {sousTitre && <div className="text-[13.5px] text-[var(--muted)] mt-1 leading-snug">{sousTitre}</div>}
          </div>
          {onFermer && (
            <button type="button" onClick={onFermer} disabled={occupe} aria-label="Fermer" data-testid={`${testId}-fermer`}
              className="w-9 h-9 grid place-items-center rounded-full shrink-0" style={{ background: "#F1F5F9" }}>
              <X size={16} />
            </button>
          )}
        </div>
        {children && <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-3 space-y-3">{children}</div>}
        {pied && (
          <div className="px-5 pt-3 shrink-0 border-t" style={{ borderColor: "var(--border)", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
            {pied}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/* Annuler / Valider, deux gros boutons côte à côte. `action` peut être asynchrone : le bouton
   dit « Un instant… » et rien ne se touche pendant ce temps. */
export const BoutonsFeuille = ({ onAnnuler, onValider, libelle, danger = false, desactive = false, occupe = false, testId = "feuille-valider" }) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: "2fr 3fr" }}>
    <button type="button" className="rounded-full min-h-[50px] font-semibold border bg-white" style={{ borderColor: "var(--border)" }}
      disabled={occupe} onClick={onAnnuler} data-testid="feuille-annuler">Annuler</button>
    <button type="button" className="rounded-full min-h-[50px] font-semibold text-white disabled:opacity-50"
      style={{ background: danger ? "#DC2626" : "var(--primary)" }} disabled={occupe || desactive}
      data-testid={testId} onClick={onValider}>
      {occupe ? "Un instant…" : libelle}
    </button>
  </div>
);

/* Une confirmation au pouce : le titre, ce qui va se passer, Annuler / Confirmer. */
export const Confirmation = ({ c, onFermer }) => {
  const [envoi, setEnvoi] = useState(false);
  if (!c) return null;
  const valider = async () => { setEnvoi(true); try { await c.action(); } finally { setEnvoi(false); } };
  return (
    <Feuille titre={c.titre} sousTitre={c.texte} onFermer={onFermer} occupe={envoi} testId="factures-confirmation"
      pied={<BoutonsFeuille onAnnuler={onFermer} onValider={valider} libelle={c.bouton} danger={c.danger}
        desactive={c.desactive} occupe={envoi} testId="factures-confirmer" />}>
      {c.contenu}
    </Feuille>
  );
};
